import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { usePageContext } from '@context/PageContext';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useSearchParams } from 'react-router-dom';

import AuditHistory from './AuditHistory';

/**
 * AuditHistoryDialog
 *
 * A self-contained dialog wrapper for the AuditHistory component that derives its
 * open state and view type from URL search parameters. This allows parent components
 * to open audit history by simply setting search params (e.g., `setSearchParams({ history: 'page' })`)
 * without navigating to a separate route — preserving unsaved state in the parent view.
 *
 * Supported search param combinations:
 * - `?history=deck` — Opens Deck Audit History
 * - `?history=page` — Opens Page Audit History
 * - `?history=deckline&decklineId=<id>` — Opens Deckline Audit History for a specific deckline
 *
 * AuditHistory resolves its view type from useParams() (route params) first, then
 * falls back to the searchParams overrides passed as props. Since this dialog renders
 * within the same route, useParams() still provides deckId and pageName from the
 * parent route match.
 */
const AuditHistoryDialog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentDeck } = useDeckContext();
  const { currentCorePageName } = usePageContext();
  const { pageDecklines } = useDecklinesContext();

  const historyParam = searchParams.get('history');
  const decklineId = searchParams.get('decklineId');

  const isOpen = historyParam === 'deck' || historyParam === 'page' || historyParam === 'deckline';

  const handleClose = () => {
    setSearchParams({});
  };

  /**
   * Derive the dialog title based on the history type, including the scope name
   */
  const getTitle = () => {
    if (historyParam === 'deckline') {
      const deckline = pageDecklines.find((dl) => dl.deckline_id === decklineId);
      const decklineName = deckline?.kpi_name ?? decklineId ?? '';
      return `Deckline Audit History: ${decklineName}`;
    }
    if (historyParam === 'deck') return `Deck Audit History: ${currentDeck?.deck_name ?? ''}`;
    return `Page Audit History: ${currentCorePageName ?? ''}`;
  };

  return (
    <Dialog className='modal-wrapper' open={isOpen} onClose={handleClose}>
      <DialogTitle className='modal-wrapper-title'>
        {getTitle()}
        <IconButton onClick={handleClose} className='modal-wrapper-close'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>{isOpen && <AuditHistory decklineId={decklineId ?? undefined} />}</DialogContent>
    </Dialog>
  );
};

export default AuditHistoryDialog;
